import * as React from 'react';
import {
  WorkspaceButton,
  WorkspaceTabs,
  isTypingTarget,
  workspaceTabIds,
} from '@/components/ui/workspace';
import { ModuleSelect, type ModuleOption } from '@/components/requirement-import/module-select';
import type { DecisionInput, MatchRun } from '@/lib/api/requirement-matches';
import { MatchRowCard } from './match-row';
import {
  GROUP_HINT,
  NOT_MAPPED_LABEL,
  areasWithoutModule,
  bulkDecisions,
  firstGroup,
  groupTabs,
  isEditable,
  plural,
  rowsOf,
  type BulkAction,
  type MatchGroup,
} from './match-logic';

/**
 * The review of one matched spreadsheet (T-0264; E14-design §2): three
 * groups (Same, ready to add; Broader; No match), each with its bulk
 * actions, and every row with its decision. "Not matched yet" appears when
 * a run stopped before the judge reached every row.
 *
 * A row added as written goes in the module its client area names, or in
 * Not mapped (ADR-025 decision 2, 24 October 2026: no module is forced on
 * it). The rows Add all as written would put in Not mapped are listed above
 * the group by client area, each with an optional module for the whole
 * area.
 *
 * Keyboard (as the requirement grids): `j` / `k` move through the rows of
 * the open group, `x` ticks or unticks a Same row and skips or un-skips
 * any other, `Enter` opens the row's other candidates.
 */

export interface MatchReviewProps {
  run: MatchRun;
  modules: ReadonlyArray<ModuleOption>;
  busy: boolean;
  onDecide: (rowId: string, input: DecisionInput) => void;
  onBulk: (decisions: ReturnType<typeof bulkDecisions>) => void;
}

const BULK: Record<MatchGroup, Array<{ action: BulkAction; label: string }>> = {
  same: [
    { action: 'add_all', label: 'Add all' },
    { action: 'skip', label: 'Skip all' },
  ],
  broader: [
    { action: 'add_library', label: 'Add all library items' },
    { action: 'add_as_written', label: 'Add all as written' },
    { action: 'skip', label: 'Skip all' },
  ],
  none: [
    { action: 'add_as_written', label: 'Add all as written' },
    { action: 'skip', label: 'Skip all' },
  ],
  unmatched: [],
};

export function MatchReview({ run, modules, busy, onDecide, onBulk }: MatchReviewProps) {
  const [group, setGroup] = React.useState<MatchGroup>(() => firstGroup(run));
  const [focused, setFocused] = React.useState(0);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const refs = React.useRef<Array<HTMLElement | null>>([]);
  const tabs = groupTabs(run);
  const rows = rowsOf(run, group);
  const ids = workspaceTabIds('library-match', group);

  // The latest rows and handlers for the key handler.
  const state = React.useRef({ rows, focused, busy, onDecide, run });
  state.current = { rows, focused, busy, onDecide, run };

  React.useEffect(() => {
    setFocused(0);
    setExpanded(null);
  }, [group]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const {
        rows: list,
        focused: at,
        busy: working,
        onDecide: decide,
        run: current,
      } = state.current;
      if (list.length === 0) return;
      const row = list[Math.min(at, list.length - 1)];
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        const next = e.key === 'j' ? Math.min(list.length - 1, at + 1) : Math.max(0, at - 1);
        setFocused(next);
        refs.current[next]?.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'x' && row && !working && isEditable(row, current)) {
        e.preventDefault();
        if (row.verdict === 'same')
          decide(row.id, row.ready ? { decision: 'skip' } : { decision: null });
        else decide(row.id, row.decision === 'skip' ? { decision: null } : { decision: 'skip' });
      } else if (e.key === 'Enter' && row) {
        // Enter on a control inside a row is the control's own.
        if (e.target instanceof HTMLElement && e.target.closest('button, select, a')) return;
        e.preventDefault();
        setExpanded((open) => (open === row.id ? null : row.id));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const editableRows = rows.filter((r) => isEditable(r, run));
  const offersAsWritten = BULK[group].some((b) => b.action === 'add_as_written');
  const missingModule = offersAsWritten ? areasWithoutModule(editableRows) : [];
  const missingCount = missingModule.reduce((n, a) => n + a.rows.length, 0);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <WorkspaceTabs
        aria-label="Match groups"
        idPrefix="library-match"
        tabs={tabs}
        value={group}
        onChange={setGroup}
      />
      <div role="tabpanel" id={ids.panel} aria-labelledby={ids.tab} className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="min-w-0 flex-1 text-body-sm text-ink-500">{GROUP_HINT[group]}</p>
          {editableRows.length > 0
            ? BULK[group].map((b) => {
                const decisions = bulkDecisions(editableRows, b.action);
                return (
                  <WorkspaceButton
                    key={b.action}
                    disabled={busy || decisions.length === 0}
                    onClick={() => onBulk(decisions)}
                  >
                    {b.label}
                  </WorkspaceButton>
                );
              })
            : null}
        </div>
        {missingCount > 0 ? (
          <section
            aria-label="Rows without a module"
            className="flex flex-col gap-2 rounded-card border border-ink-100 bg-paper-0 p-3"
          >
            <p className="text-body-sm text-ink-700">
              No library module is named for the client area of {plural(missingCount, 'row')}, so
              Add as written puts {missingCount === 1 ? 'it' : 'them'} in {NOT_MAPPED_LABEL}: kept
              with the client’s area, not rated in vendor intelligence until mapped. Map{' '}
              {missingCount === 1 ? 'it' : 'them'} later from the requirements list, or choose a
              module for an area now:
            </p>
            {missingModule.map((a) => (
              <div key={a.label} className="flex flex-wrap items-center gap-2">
                <p className="min-w-0 flex-1 text-body-sm text-ink-900">
                  {a.label}{' '}
                  <span className="text-meta text-ink-500">· {plural(a.rows.length, 'row')}</span>
                </p>
                <div className="w-72 max-w-full">
                  <ModuleSelect
                    aria-label={`Module for ${a.label}`}
                    value={null}
                    modules={modules}
                    allowSkip={false}
                    disabled={busy}
                    onChange={(id) =>
                      onBulk(
                        a.rows.map((row) => ({
                          row_id: row.id,
                          decision: 'add_as_written' as const,
                          module_id: id,
                        })),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </section>
        ) : null}
        {rows.length === 0 ? (
          <p className="rounded-card border border-ink-100 bg-paper-0 px-4 py-6 text-center text-body-sm text-ink-500">
            No {group === 'none' ? 'rows without a match' : `${group} rows`} in this spreadsheet.
          </p>
        ) : (
          <div
            aria-label={`${tabs.find((t) => t.value === group)?.label} rows`}
            className="flex flex-col gap-2.5"
          >
            {rows.map((row, i) => (
              <MatchRowCard
                key={row.id}
                ref={(el) => {
                  refs.current[i] = el;
                }}
                row={row}
                editable={isEditable(row, run)}
                busy={busy}
                focused={i === focused}
                expanded={expanded === row.id}
                modules={modules}
                onExpandedChange={(open) => setExpanded(open ? row.id : null)}
                onDecide={(input) => onDecide(row.id, input)}
                onFocus={() => setFocused(i)}
              />
            ))}
          </div>
        )}
        <p className="text-meta text-ink-500">
          {plural(rows.length, 'row')} · <kbd className="kbd">j</kbd> <kbd className="kbd">k</kbd>{' '}
          move · <kbd className="kbd">x</kbd> add or skip · <kbd className="kbd">Enter</kbd> other
          candidates
        </p>
      </div>
    </div>
  );
}
